import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import './LiveMarketTicker.css';

const DEFAULT_TICKER_ITEMS = [
  { symbol: 'BTC', slug: 'btc', name: 'Bitcoin', price: 83112, change24h: 0.40, category: 'crypto' },
  { symbol: 'ETH', slug: 'eth', name: 'Ethereum', price: 2505.16, change24h: 0.49, category: 'crypto' },
  { symbol: 'SOL', slug: 'sol', name: 'Solana', price: 212.80, change24h: 5.14, category: 'crypto' },
  { symbol: 'NVDA', slug: 'nvda', name: 'NVIDIA', price: 229.28, change24h: -2.00, category: 'stocks' },
  { symbol: 'S&P 500', slug: 'sp500', name: 'S&P 500', price: 7811.54, change24h: 1.15, category: 'indices' },
  { symbol: 'XAU', slug: 'gold', name: 'Gold Bullion', price: 4216.30, change24h: 0.70, category: 'commodities' },
  { symbol: 'AAPL', slug: 'aapl', name: 'Apple', price: 336.64, change24h: 0.88, category: 'stocks' },
  { symbol: 'TSLA', slug: 'tsla', name: 'Tesla', price: 352.80, change24h: 4.85, category: 'stocks' },
  { symbol: 'WTI', slug: 'crude-oil', name: 'Crude Oil', price: 91.85, change24h: 2.69, category: 'commodities' },
  { symbol: 'MSFT', slug: 'msft', name: 'Microsoft', price: 535.07, change24h: 3.39, category: 'stocks' },
  { symbol: 'EUR/USD', slug: 'eur-usd', name: 'Euro / USD', price: 1.1206, change24h: -0.44, category: 'forex' }
];

function formatPrice(val, symbol) {
  if (typeof val !== 'number' || isNaN(val)) return '--';
  if (symbol === 'EUR/USD' || symbol === 'GBP/USD') return val.toFixed(4);
  if (val >= 1000) return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return '$' + val.toFixed(2);
}

export function LiveMarketTicker({ initialOverview = null }) {
  const [items, setItems] = useState(() => {
    if (initialOverview?.heroKpis && Array.isArray(initialOverview.heroKpis) && initialOverview.heroKpis.length > 0) {
      const heroMap = new Map(initialOverview.heroKpis.map(k => [k.symbol, k]));
      return DEFAULT_TICKER_ITEMS.map(item => {
        if (heroMap.has(item.symbol)) {
          const live = heroMap.get(item.symbol);
          return { ...item, price: live.price, change24h: live.change24h };
        }
        return item;
      });
    }
    return DEFAULT_TICKER_ITEMS;
  });

  // Client-side auto-refresh every 60 seconds from /api/markets/overview
  useEffect(() => {
    let isMounted = true;

    const refreshTicker = () => {
      fetch('/api/markets/overview')
        .then(r => r.json())
        .then(data => {
          if (!isMounted || !data?.ok) return;
          const heroMap = new Map((data.heroKpis || []).map(k => [k.symbol, k]));
          const trendingMap = new Map((data.trending || []).map(k => [k.symbol, k]));

          setItems(prev => prev.map(item => {
            const live = heroMap.get(item.symbol) || trendingMap.get(item.symbol);
            if (live && typeof live.price === 'number') {
              return { ...item, price: live.price, change24h: live.change24h };
            }
            return item;
          }));
        })
        .catch(() => {});
    };

    // Refresh immediately on mount to grab live rates
    refreshTicker();

    const interval = setInterval(refreshTicker, 60000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Duplicate items array for continuous seamless infinite marquee loop
  const duplicated = [...items, ...items];

  return (
    <div className="bza-ticker-bar" role="region" aria-label="Live Market Price Updates">
      {/* Live Flickering Indicator Badge */}
      <div className="bza-ticker-badge">
        <span className="bza-ticker-live-dot" aria-hidden="true" />
        <span className="bza-ticker-badge-text">LIVE</span>
      </div>

      {/* Infinite Scrolling Ticker Track */}
      <div className="bza-ticker-track-wrapper">
        <div className="bza-ticker-track">
          {duplicated.map((item, idx) => {
            const isPositive = (item.change24h || 0) >= 0;
            const assetUrl = `/markets/asset/${item.slug || item.symbol.toLowerCase()}/`;
            const isBtc = item.symbol === 'BTC';

            return (
              <a
                key={`${item.symbol}-${idx}`}
                href={assetUrl}
                className={`bza-ticker-item ${isBtc ? 'bza-ticker-btc' : ''}`}
                title={`View ${item.name} (${item.symbol}) market intelligence`}
              >
                {isBtc && <span className="bza-ticker-btc-badge" aria-hidden="true">₿</span>}
                <span className="bza-ticker-symbol">{item.symbol}</span>
                <span className="bza-ticker-price">{formatPrice(item.price, item.symbol)}</span>
                <span className={`bza-ticker-change ${isPositive ? 'positive' : 'negative'}`}>
                  {isPositive ? <TrendingUp size={11} strokeWidth={2.5} /> : <TrendingDown size={11} strokeWidth={2.5} />}
                  <span>{isPositive ? '+' : ''}{Number(item.change24h || 0).toFixed(2)}%</span>
                </span>
              </a>
            );
          })}
        </div>
      </div>

      {/* All Markets Quick Access Link */}
      <a href="/markets/" className="bza-ticker-view-all" title="Open complete Market Intelligence Platform">
        <span>Markets</span>
        <ArrowRight size={12} />
      </a>
    </div>
  );
}

export default LiveMarketTicker;
